import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { useEffect } from "react";

import copy from "../../public/locales/en/analytics.json";
import { useAppDispatch, useAppSelector } from "../store";
import AnalyticsRowsTable from "./AnalyticsRowsTable";
import {
  fetchLanguageDemand,
  setDemandCountry,
} from "../store/slices/productAnalyticsSlice";
import { countryName } from "../utils/analyticsFormatting";

import type { ReactElement } from "react";

const labels = copy.demand;
const languages = new Intl.DisplayNames("en", { type: "language" });
function languageName(code: string | null): string {
  if (!code) return labels.unknownLanguage;
  try {
    return languages.of(code) ?? code;
  } catch {
    return code;
  }
}
export default function LanguageDemandReport(): ReactElement {
  const dispatch = useAppDispatch();
  const {
    days,
    demand: report,
    demandError: error,
    demandCountry: country,
  } = useAppSelector((state) => state.productAnalytics);
  useEffect((): (() => void) => {
    const request = dispatch(fetchLanguageDemand(days));
    return (): void => request.abort();
  }, [dispatch, days]);
  const landing = (report?.landing ?? []).filter(
    (row): boolean => country === "all" || (row.country ?? "") === country,
  );
  const registrations = (report?.registrations ?? []).filter(
    (row): boolean => country === "all" || (row.country ?? "") === country,
  );
  const countries = Array.from(
    new Set(
      [...(report?.landing ?? []), ...(report?.registrations ?? [])].map(
        (row): string => row.country ?? "",
      ),
    ),
  ).sort();
  if (country !== "all" && !countries.includes(country))
    countries.push(country);
  return (
    <Paper component="section" sx={{ p: { xs: 2, md: 3 }, mt: 3 }}>
      <Typography component="h2" variant="h5">
        {labels.title}
      </Typography>
      <Typography sx={{ my: 2 }} color="text.secondary">
        {labels.note}
      </Typography>
      {error && <Alert severity="error">{labels.error}</Alert>}
      {!report && !error && <CircularProgress aria-label={labels.loading} />}
      {report && (
        <>
          <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
            <InputLabel id="demand-country">{labels.country}</InputLabel>
            <Select
              labelId="demand-country"
              label={labels.country}
              value={country}
              onChange={(event): void => {
                dispatch(setDemandCountry(event.target.value));
              }}
            >
              <MenuItem value="all">{labels.allCountries}</MenuItem>
              {countries.map(
                (value): ReactElement => (
                  <MenuItem key={value} value={value}>
                    {countryName(value || null)}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
          <Box sx={{ mb: 3 }}>
            <Typography component="h3" variant="h6">
              {labels.landingTitle}
            </Typography>
            <Typography variant="body2" sx={{ my: 2 }}>
              {labels.landingNote}
            </Typography>
            {report.firstLandingRecordedDate && (
              <Typography variant="body2">
                {labels.firstRecorded}: {report.firstLandingRecordedDate}
              </Typography>
            )}
            {landing.length === 0 ? (
              <Typography>{labels.empty}</Typography>
            ) : (
              <AnalyticsRowsTable
                label={labels.landingTitle}
                rows={landing}
                columns={[
                  {
                    label: labels.country,
                    value: (row): string => countryName(row.country),
                  },
                  {
                    label: labels.locale,
                    value: (row): string => languageName(row.locale),
                  },
                  { label: copy.views, value: (row): number => row.views },
                ]}
              />
            )}
          </Box>
          <Typography component="h3" variant="h6">
            {labels.registrationsTitle}
          </Typography>
          <Typography variant="body2" sx={{ my: 2 }}>
            {labels.registrationsNote}
          </Typography>
          {registrations.length === 0 ? (
            <Typography>{labels.empty}</Typography>
          ) : (
            <AnalyticsRowsTable
              label={labels.registrationsTitle}
              rows={registrations}
              columns={[
                {
                  label: labels.country,
                  value: (row): string => countryName(row.country),
                },
                {
                  label: labels.native,
                  value: (row): string => languageName(row.nativeLanguage),
                },
                {
                  label: labels.target,
                  value: (row): string => languageName(row.targetLanguage),
                },
                {
                  label: labels.basis,
                  value: (row): string =>
                    row.languageBasis === "registration_snapshot"
                      ? labels.snapshot
                      : labels.legacy,
                },
                {
                  label: copy.campaign.registrations,
                  value: (row): number => row.registrations,
                },
              ]}
            />
          )}
        </>
      )}
    </Paper>
  );
}
